'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UIButton from 'UIComponents/button/UIButton';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import ExtendedLanguageSelect from 'DesignEditorsUI/components/shared/localization/ExtendedLanguageSelect';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import ChangeMasterLanguageModal from 'DesignEditorsUI/components/shared/localization/ChangeMasterLanguageModal';
import LanguageButton from 'DesignEditorsUI/components/shared/localization/LanguageButton';
import DeleteLanguageModal from 'DesignEditorsUI/components/shared/localization/DeleteLanguageModal';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as CustomWidgetBuffersActions from 'DesignData/actions/CustomWidgetBuffers';
import { getPortalLanguage } from 'DesignData/selectors/i18n';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.translations.${key}`;

function makeMapStateToProps() {
  const getMessages = customWidgetBuffersSelectors.makeGetMessages();
  const getMasterLanguage = customWidgetBuffersSelectors.makeGetMasterLanguage();
  return (state, props) => {
    const messages = getMessages(state, props);
    return {
      messages,
      selectedLanguageEditor: customWidgetEditorOptionsSelectors.getSelectedLanguageEditor(state, props),
      masterLanguage: getMasterLanguage(state, props),
      languages: Object.keys(messages),
      portalLanguage: getPortalLanguage(state)
    };
  };
}

const mapDispatchToProps = {
  setLanguageEditor: CustomWidgetEditorOptionsActions.setLanguageEditor,
  addLanguage: CustomWidgetBuffersActions.addLanguage,
  deleteLanguage: CustomWidgetBuffersActions.deleteLanguage,
  changeMasterLanguage: CustomWidgetBuffersActions.changeMasterLanguage,
  optInTranslations: CustomWidgetBuffersActions.optInTranslations,
  regenerateTranslations: CustomWidgetBuffersActions.regenerateTranslations
};

class Translations extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      selectedMasterLanguage: null,
      languageToDelete: null
    };

    this.validateSourceTranslations = () => {
      const {
        customWidgetId,
        customWidgetLabel,
        masterLanguage,
        messages,
        regenerateTranslations
      } = this.props;

      if (masterLanguage && !messages[masterLanguage]) {
        regenerateTranslations(customWidgetId, customWidgetLabel, masterLanguage);
      }
    };

    this.onDeleteLanguageClick = lang => {
      this.setState({
        languageToDelete: lang
      });
    };

    this.onChangeMasterLanguageClick = () => {
      this.setState({
        selectedMasterLanguage: this.props.masterLanguage
      });
    };

    this.onChangeMasterLanguageCancel = () => {
      this.setState({
        selectedMasterLanguage: null
      });
    };

    this.onMasterLanguageChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        selectedMasterLanguage: value
      });
    };

    this.onMasterLanguageChangeConfirm = () => {
      const {
        changeMasterLanguage,
        customWidgetId,
        customWidgetLabel
      } = this.props;
      const {
        selectedMasterLanguage
      } = this.state;
      changeMasterLanguage(customWidgetId, customWidgetLabel, selectedMasterLanguage);
      this.onChangeMasterLanguageCancel();
    };

    this.setOpenEditor = lang => {
      this.props.setLanguageEditor(this.props.customWidgetId, lang);
    };

    this.enableTranslations = () => {
      const {
        customWidgetId,
        customWidgetLabel,
        optInTranslations,
        portalLanguage
      } = this.props;
      optInTranslations(customWidgetId, customWidgetLabel, portalLanguage);
    };

    this.onAddLanguage = evt => {
      const {
        addLanguage,
        customWidgetId,
        customWidgetLabel
      } = this.props;
      addLanguage(customWidgetId, customWidgetLabel, evt.target.value);
    };

    this.onDeleteLanguageCancel = () => {
      this.setState({
        languageToDelete: null
      });
    };

    this.onDeleteLanguage = () => {
      this.props.deleteLanguage(this.props.customWidgetId, this.state.languageToDelete);
      this.onDeleteLanguageCancel();
    };
  }

  componentDidMount() {
    this.validateSourceTranslations();
  }

  get disabled() {
    return this.props.isReadOnly || !!this.props.revisionId;
  }

  renderDeleteModal() {
    const {
      languageToDelete
    } = this.state;
    if (!languageToDelete) return null;
    return /*#__PURE__*/_jsx(DeleteLanguageModal, {
      languageToDelete: languageToDelete,
      i18nKey: i18nKey,
      onDeleteLanguage: this.onDeleteLanguage,
      onDeleteLanguageCancel: this.onDeleteLanguageCancel,
      disabled: this.disabled
    });
  }

  renderChangeMasterLanguageModal() {
    if (!this.state.selectedMasterLanguage) return null;
    return /*#__PURE__*/_jsx(ChangeMasterLanguageModal, {
      i18nKey: i18nKey,
      masterLanguage: this.props.masterLanguage,
      onChangeMasterLanguageCancel: this.onChangeMasterLanguageCancel,
      onMasterLanguageChange: this.onMasterLanguageChange,
      onMasterLanguageChangeConfirm: this.onMasterLanguageChangeConfirm
    });
  }

  renderLanguages() {
    const {
      masterLanguage,
      languages,
      selectedLanguageEditor
    } = this.props;
    return languages.map(language => {
      return /*#__PURE__*/_jsx(LanguageButton, {
        language: language,
        selectedLanguage: selectedLanguageEditor,
        onClick: this.setOpenEditor,
        masterLanguage: masterLanguage,
        onChangeMasterLanguageClick: this.onChangeMasterLanguageClick,
        onDeleteLanguageClick: this.onDeleteLanguageClick,
        disableActionButtons: this.disabled
      }, language);
    });
  }

  renderOptIn() {
    return /*#__PURE__*/_jsxs("div", {
      className: "opt-in",
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('optIn.label')
        })
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('optIn.help')
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        className: "p-top-3",
        use: "primary",
        onClick: this.enableTranslations,
        disabled: this.disabled,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('optIn.button')
        })
      })]
    });
  }

  renderLabel() {
    const omittedLanguages = [this.props.masterLanguage, ...this.props.languages];
    return /*#__PURE__*/_jsx(UIFormLabel, {
      className: "translations-label",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        justify: "between",
        children: [/*#__PURE__*/_jsx(UIBox, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('header.label'),
            options: {
              count: this.props.languages.length
            }
          })
        }), /*#__PURE__*/_jsx(UIBox, {
          children: /*#__PURE__*/_jsx(ExtendedLanguageSelect, {
            anchorType: "button",
            buttonUse: "link",
            closeOnTargetLeave: true,
            disabled: this.disabled,
            value: I18n.text(i18nKey('header.select')),
            omittedLanguages: omittedLanguages,
            onChange: this.onAddLanguage,
            placement: "bottom left"
          })
        })]
      })
    });
  }

  render() {
    if (!this.props.portalLanguage) return null;

    if (!this.props.masterLanguage) {
      return this.renderOptIn();
    }

    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderLabel(), this.renderLanguages(), this.renderChangeMasterLanguageModal(), this.renderDeleteModal()]
    });
  }

}

Translations.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  customWidgetLabel: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  masterLanguage: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string),
  setLanguageEditor: PropTypes.func.isRequired,
  selectedLanguageEditor: PropTypes.string,
  portalLanguage: PropTypes.string,
  addLanguage: PropTypes.func.isRequired,
  deleteLanguage: PropTypes.func.isRequired,
  changeMasterLanguage: PropTypes.func.isRequired,
  optInTranslations: PropTypes.func.isRequired,
  regenerateTranslations: PropTypes.func.isRequired,
  revisionId: PropTypes.number,
  isReadOnly: PropTypes.bool
};
Translations.defaultProps = {
  languages: []
};
export default connect(makeMapStateToProps, mapDispatchToProps)(Translations);